import React from "react";
import { DialogClose } from "../ui/dialog";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Input } from "../ui/input";
import z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { useCreatePriceAlert } from "@/hooks/useCreatePriceAlert";
import { toast } from "sonner";
import {
  Activity,
  Bell,
  Target,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { ResponsiveModal } from "../ResponsiveModal/ResponsiveModal";

type PriceAlertAction = "any" | "increase" | "decrease" | "below_market";

type Props = React.PropsWithChildren & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cardId: number;
  defaultAction?: PriceAlertAction;
  defaultPrice?: number;
  onCreated?: (alert: { action: PriceAlertAction; price: number }) => void;
};

export const CardPriceAlertDialog: React.FC<Props> = ({
  cardId,
  open,
  onOpenChange,
  defaultAction = "below_market",
  defaultPrice = 20,
  onCreated,
}) => {
  const priceAlertSchema = z
    .object({
      action: z.enum(["any", "increase", "decrease", "below_market"]),
      price: z.coerce.number().min(0),
    })
    .superRefine((value, context) => {
      if (
        value.action === "below_market" &&
        (value.price < 1 || value.price > 90)
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["price"],
          message: "Choose a discount between 1% and 90%",
        });
      }

      if (value.action !== "any" && value.price <= 0) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["price"],
          message: "Enter a value greater than zero",
        });
      }
    });

  const form = useForm<z.infer<typeof priceAlertSchema>>({
    resolver: zodResolver(priceAlertSchema),
    defaultValues: {
      action: defaultAction,
      price: defaultPrice,
    },
  });

  const { mutateAsync, isPending } = useCreatePriceAlert();

  const action = form.watch("action");
  const isDealHunt = action === "below_market";

  const handleSubmit = form.handleSubmit(async (data) => {
    await mutateAsync(
      { ...data, cardId },
      {
        onSuccess() {
          toast(
            data.action === "below_market"
              ? "Deal hunt is active!"
              : "Price alert has been added!",
          );
        },
      },
    );
    onCreated?.({ action: data.action, price: data.price });
    form.reset({ action: defaultAction, price: defaultPrice });
    onOpenChange(false);
  });

  const getTriggerIcon = (trigger: PriceAlertAction) => {
    switch (trigger) {
      case "increase":
        return <TrendingUp className="size-4 text-green-500" />;
      case "decrease":
        return <TrendingDown className="size-4 text-red-500" />;
      case "below_market":
        return <Target className="size-4 text-indigo-500" />;
      case "any":
        return <Activity className="size-4 text-blue-500" />;
      default:
        return <Bell className="size-4" />;
    }
  };

  const triggers: Array<{
    value: PriceAlertAction;
    label: string;
    icon: React.ReactNode;
  }> = [
    {
      value: "below_market",
      label: "Deal: below 30-day average",
      icon: getTriggerIcon("below_market"),
    },
    {
      value: "decrease",
      label: "Drops to a target price",
      icon: getTriggerIcon("decrease"),
    },
    {
      value: "increase",
      label: "Rises to a target price",
      icon: getTriggerIcon("increase"),
    },
    {
      value: "any",
      label: "Any price change",
      icon: getTriggerIcon("any"),
    },
  ];

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={isDealHunt ? "Create Deal Hunt" : "Create Price Alert"}
      repositionInputs={false}
    >
      <Form {...form}>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <FormField
            control={form.control}
            name="action"
            render={({ field }) => (
              <FormItem>
                <FormLabel>What should we watch for?</FormLabel>
                <Select
                  onValueChange={(value) => {
                    field.onChange(value);
                    form.setValue(
                      "price",
                      value === "below_market" ? 20 : value === "any" ? 0 : 1,
                    );
                  }}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger disabled={isPending}>
                      <SelectValue placeholder="Select a trigger" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {triggers.map((trigger) => (
                      <SelectItem key={trigger.value} value={trigger.value}>
                        <div className="flex items-center gap-2">
                          {trigger.icon}
                          {trigger.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {action !== "any" && (
            <FormField
              control={form.control}
              name="price"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {isDealHunt
                      ? "Minimum discount from 30-day average"
                      : "Target price"}
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      {!isDealHunt && (
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                          $
                        </span>
                      )}
                      <Input
                        id="price"
                        type="number"
                        className={isDealHunt ? "pr-8" : "pl-8"}
                        step={isDealHunt ? "1" : "0.01"}
                        min={isDealHunt ? "1" : "0.01"}
                        max={isDealHunt ? "90" : undefined}
                        placeholder={isDealHunt ? "20" : "0.00"}
                        disabled={isPending}
                        {...field}
                      />
                      {isDealHunt && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                          %
                        </span>
                      )}
                    </div>
                  </FormControl>
                  {isDealHunt && (
                    <p className="text-xs leading-5 text-muted-foreground">
                      We compare current variants with their 30-day average and
                      alert you when the discount reaches this percentage.
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {(action === "any" ? true : form.watch("price") > 0) && (
            <div className="rounded-lg bg-muted p-3">
              <div className="flex items-start gap-2 text-sm">
                <span className="mt-0.5">{getTriggerIcon(action)}</span>
                <span className="text-muted-foreground">
                  {action === "below_market" ? (
                    <>
                      Alert when a tracked variant is{" "}
                      <span className="font-medium text-foreground">
                        {form.watch("price")}% or more below
                      </span>{" "}
                      its 30-day average.
                    </>
                  ) : action === "any" ? (
                    "Alert whenever the market price changes."
                  ) : (
                    <>
                      Alert when the price{" "}
                      {action === "increase" ? "rises to" : "drops to"}{" "}
                      <span className="font-medium text-foreground">
                        {"$"}
                        {form.watch("price")}
                      </span>
                      .
                    </>
                  )}
                </span>
              </div>
            </div>
          )}

          <div className="inline-flex w-full gap-4">
            <DialogClose asChild>
              <Button variant="outline" className="ml-auto">
                Close
              </Button>
            </DialogClose>
            <Button
              className="bg-electric-yellow text-deep-indigo hover:bg-electric-yellow/90 font-medium"
              disabled={isPending}
            >
              {isPending
                ? "Creating..."
                : isDealHunt
                  ? "Start hunt"
                  : "Create alert"}
            </Button>
          </div>
        </form>
      </Form>
    </ResponsiveModal>
  );
};
